import { useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthenticateUser } from "~/api/authentication/authentication";
import { getGetCurrentUserQueryKey } from "~/api/users/users";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  removeAllTokens,
  ROUTES,
  storeAccessToken,
  storeRefreshToken,
  storeUsername,
  validateEmail,
} from "~/helpers";
import { trackEvent } from "~/utils/analytics";

interface FormErrors {
  email?: boolean;
  password?: boolean;
}

export const LoginForm = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const { mutate, isPending, isError } = useAuthenticateUser();
  const queryClient = useQueryClient();

  useEffect(() => {
    const username = searchParams.get("username");
    if (username) {
      const usernameWithPlus = username.replace(/ /g, "+");
      setEmail(decodeURIComponent(usernameWithPlus));
    }
  }, [searchParams]);

  const validateForm = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!validateEmail(email)) {
      newErrors.email = true;
      isValid = false;
    }

    if (!password.trim()) {
      newErrors.password = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const onLoginFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    removeAllTokens();
    queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    queryClient.invalidateQueries();

    trackEvent({ eventName: "app/login/log_in.button/submit" });
    mutate(
      { data: { email, password } },
      {
        onSuccess: (data) => {
          if (
            "challengeName" in data &&
            data.challengeName === "NEW_PASSWORD_REQUIRED"
          ) {
            navigate(ROUTES.CHANGE_PASSWORD, {
              state: {
                session: "session" in data ? data.session : undefined,
                username: "username" in data ? data.username : undefined,
              },
            });
            return;
          }

          if ("accessToken" in data && "refreshToken" in data) {
            storeAccessToken(data.accessToken);
            storeRefreshToken(data.refreshToken);
            storeUsername(data.username);
            const retpath = searchParams.get("retpath");
            navigate(retpath ? decodeURIComponent(retpath) : "/");
          } else {
            throw new Error("No access token or refresh token");
          }
        },
        onError: () => {
          trackEvent({ eventName: "app/login/error" });
        },
      }
    );
  };

  return (
    <div className="flex justify-center items-start pt-10">
      <form onSubmit={onLoginFormSubmit} className="w-full max-w-lg">
        <h3 className="text-2xl font-semibold mb-6 text-center text-gray-800">
          <FormattedMessage id="loginForm.title" defaultMessage="Login" />
        </h3>
        <div className="mb-4">
          <Label
            htmlFor="loginUsername"
            className="block text-sm font-medium text-gray-700 mb-2 leading-none"
          >
            <FormattedMessage id="loginForm.username" defaultMessage="Email" />
          </Label>
          <Input
            id="loginUsername"
            placeholder={intl.formatMessage({
              id: "loginForm.usernamePlaceholder",
              defaultMessage: "Input your email",
            })}
            value={email}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
              errors.email || isError ? "border-red-600" : "border-gray-300"
            }`}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && (
            <span className="text-red-600 text-sm mt-2 block">
              <FormattedMessage
                id="auth.register.emailError"
                defaultMessage="Email should be <strong>example@site.com</strong>"
                values={{
                  strong: (chunks) => (
                    <strong className="font-semibold">{chunks}</strong>
                  ),
                }}
              />
            </span>
          )}
        </div>
        <div>
          <Label
            htmlFor="loginPassword"
            className="block text-sm font-medium text-gray-700 mb-2 leading-none"
          >
            <FormattedMessage
              id="loginForm.password"
              defaultMessage="Password"
            />
          </Label>
          <Input
            id="loginPassword"
            type="password"
            placeholder={intl.formatMessage({
              id: "loginForm.passwordPlaceholder",
              defaultMessage: "Input your password",
            })}
            value={password}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
              errors.password || isError ? "border-red-600" : "border-gray-300"
            }`}
            onChange={(e) => setPassword(e.target.value)}
          />
          {errors.password && (
            <span className="text-red-600 text-sm mt-2 block">
              <FormattedMessage
                id="loginForm.passwordRequired"
                defaultMessage="Please enter the password"
              />
            </span>
          )}
          {isError && (
            <span className="text-red-600 text-sm mt-2 block">
              <FormattedMessage
                id="loginForm.errorIncorrect"
                defaultMessage="Email or password incorrect"
              />
            </span>
          )}
        </div>
        <div className="text-sm py-[16px]">
          <Link
            to={ROUTES.FORGOT_PASSWORD}
            onClick={() =>
              trackEvent({ eventName: "app/login/forgot_password.link/click" })
            }
            className="text-blue-600 hover:text-blue-700"
          >
            Forgot password?
          </Link>
        </div>
        <div className="flex flex-col gap-4">
          <Button
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300"
            type="submit"
            disabled={isPending}
          >
            <FormattedMessage id="loginForm.login" defaultMessage="Log In" />
          </Button>
          <span className="text-sm text-muted-foreground font-medium text-center">
            <FormattedMessage id="auth.register.or" defaultMessage="or" />
          </span>
          <Button variant="secondary" asChild>
            <Link to={ROUTES.REGISTER}>
              <FormattedMessage
                id="auth.register.createAccount"
                defaultMessage="Create Account"
              />
            </Link>
          </Button>
        </div>
      </form>
    </div>
  );
};
